/**
 * 데이터모델러 — 외부 (QBase 테스트 센터 / Playwright runner) 가 호출할 수 있는 hook.
 *
 * 노출 위치 : window.__appTestHooks
 * 노출 조건 : NODE_ENV !== 'production', 또는 빌드 시 REACT_APP_TEST_HOOKS=true
 *             (운영 빌드 기본값은 undefined. 테스트 전용 빌드에서만 켠다 — 운영 도메인을
 *              프로덕션 빌드로 바꾸면 callTestHook 시나리오가 깨지므로, 테스트 대상은 이 값으로 빌드)
 *
 * 안전성 : 인증을 우회하는 hook 은 없다. login 은 로그인하지 않고 상태만 돌려주며,
 *          나머지는 화면 이벤트(EventBus)만 보낸다 — 서버 저장은 하지 않는다.
 *
 * Playwright runner 의 step type 'callTestHook' 이 이 hook 들을 page.evaluate 로 호출.
 *
 * 시그니처 규약 :
 *   - hook 은 동기 또는 Promise 반환 (Playwright 는 await 가능)
 *   - 예외는 throw 로 — runner 가 stack 을 캡처해 stepResult.errorMsg 에 기록
 *   - 반환값은 단순 직렬화 가능한 값 (string/number/boolean/object/array)
 *
 * 실 동작 wiring : 데이터모델러의 EventBus 이벤트로 다이어그램 조작.
 *   - ENTITY-ADD       (nodeEventHandlers.handleEntityAdd)
 *   - ATTRIBUTE-ADD    (attributeEventHandlers.handleAttributeAdd)
 *   - RELATIONSHIP-ADD (relationshipEventHandlers.handleRelationshipAdd)
 *   - MEMO-ADD         (nodeEventHandlers.handleMemoAdd)
 */

import EventBus from 'utils/hooks/EventBus';

const APPLY = () => {
  if (typeof window === 'undefined') return;
  if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_TEST_HOOKS !== 'true') return;

  const getStore = () => window.__REDUX_STORE__;

  const requireStore = () => {
    const s = getStore();
    if (!s) throw new Error('REDUX_STORE 미준비 — 페이지 마운트 직후일 가능성');
    return s;
  };

  /** 진단/유틸 */
  const hooks = {
    ping() {
      return { ok: true, ts: Date.now() };
    },

    login(usrid) {
      const state = requireStore().getState();
      const currentUserId = state.auth.user.userId;
      if (currentUserId) {
        return { alreadyLoggedIn: true, userId: currentUserId };
      }
      // 실제 로그인은 SignInForm 의 폼 submit 으로 처리. 본 hook 은 상태 확인 용
      return { alreadyLoggedIn: false, requested: usrid || null };
    },

    // 스토어 경로: state.base.common.modelInfo (store/base/commonSlice.ts).
    // 이전에는 존재하지 않는 state.diagram / state.nodes 를 읽어 항상 0 / null 을 돌려줬다(2026-09-14 수정).

    /** 모든 노드 수 (entity + memo + image 등) */
    getNodeCount() {
      return requireStore().getState().base.common.modelInfo.nodes.length;
    },

    /** 엔터티 노드만 카운트 */
    getEntityCount() {
      return requireStore().getState().base.common.modelInfo.nodes
        .filter(n => n.type === 'entityNode').length;
    },

    /** 현재 모델 메타 정보 — 키는 스토어 필드명 그대로 (ModelInfo 타입) */
    getCurrentModelInfo() {
      const info = requireStore().getState().base.common.modelInfo;
      return {
        mdlrmdlId: info.mdlrmdlId,
        modelName: info.modelName,
        changed: info.changed,
      };
    },

    /**
     * 엔터티 노드 1개 추가.
     *   args : { name, physical?, description?, x?, y? }
     */
    createEntity(args = {}) {
      requireStore();
      const payload = {
        entityName:        args.name        || '새 엔터티',
        physicalName:      args.physical    || '',
        entityDescription: args.description || '',
        position: {
          x: typeof args.x === 'number' ? args.x : 120,
          y: typeof args.y === 'number' ? args.y : 120,
        },
      };
      EventBus.emit('ENTITY-ADD', payload);
      return { ok: true, emitted: 'ENTITY-ADD', payload };
    },

    /**
     * 엔터티에 속성 1개 추가.
     *   args : { entityName, attributeName, physical?, dataType?, isIdentifier? }
     */
    addAttribute(args = {}) {
      requireStore();
      if (!args.entityName) throw new Error('addAttribute: entityName 필수');
      if (!args.attributeName) throw new Error('addAttribute: attributeName 필수');
      const payload = {
        entityName:    args.entityName,
        attributeName: args.attributeName,
        physicalName:  args.physical   || '',
        dataType:      args.dataType   || 'VARCHAR',
        isIdentifier:  !!args.isIdentifier,
      };
      EventBus.emit('ATTRIBUTE-ADD', payload);
      return { ok: true, emitted: 'ATTRIBUTE-ADD', payload };
    },

    /**
     * 두 엔터티 사이 관계 추가.
     *   args : { fromEntityName, toEntityName, relationType? } — relationType: 'IR'(식별) | 'NR'(비식별)
     *   id 가 아니라 이름으로 받음 (시나리오 가독성). 내부에서 nodes 에서 id 조회.
     */
    addRelationship(args = {}) {
      const store = requireStore();
      if (!args.fromEntityName) throw new Error('addRelationship: fromEntityName 필수');
      if (!args.toEntityName)   throw new Error('addRelationship: toEntityName 필수');
      const state = store.getState();
      // 다양한 store 모양 모두 시도 — 어디 nodes 가 있는지 dump 로 진단 가능
      // 실제 store 경로: state.base.common.modelInfo.nodes (commonSlice)
      const nodes = state?.base?.common?.modelInfo?.nodes || [];
      const isEntity = (n) => {
        const t = n?.type || '';
        return t === 'entity' || t === 'entityNode' || t.toLowerCase().includes('entity');
      };
      // entity 이름은 여러 키에 들어있을 수 있음 — 모두 시도
      const nameOf = (n) =>
        n?.data?.entityName || n?.data?.label || n?.data?.name || n?.data?.nm
        || n?.data?.title || n?.data?.entity?.name || '';
      const find = (nm) => nodes.find(n => isEntity(n) && nameOf(n) === nm);
      const src = find(args.fromEntityName);
      const tgt = find(args.toEntityName);
      if (!src || !tgt) {
        const sample = nodes.slice(0, 10).map(n => ({
          id: n?.id, type: n?.type, name: nameOf(n),
          dataKeys: n?.data ? Object.keys(n.data).slice(0, 8) : []
        }));
        const where = !src ? 'from' : 'to';
        const missing = !src ? args.fromEntityName : args.toEntityName;
        throw new Error(
          `addRelationship: ${where} entity 미발견 "${missing}". `
          + `nodes(${nodes.length}) sample=${JSON.stringify(sample)}`
        );
      }
      const payload = {
        sourceEntityId: src.id,
        targetEntityId: tgt.id,
        relationType:   args.relationType || 'NR',
      };
      EventBus.emit('RELATIONSHIP-ADD', payload);
      return { ok: true, emitted: 'RELATIONSHIP-ADD', payload };
    },

    /**
     * 메모 노드 추가.
     *   args : { text?, x?, y? }
     */
    createMemo(args = {}) {
      requireStore();
      // MEMO-ADD 핸들러(createNewMemoNode) 가 읽는 키: title / content / position
      const payload = {
        title:   args.title || '',
        content: args.text  || '',
      };
      if (typeof args.x === 'number' && typeof args.y === 'number') {
        payload.position = { x: args.x, y: args.y };
      }
      EventBus.emit('MEMO-ADD', payload);
      return { ok: true, emitted: 'MEMO-ADD', payload };
    },
  };

  window.__appTestHooks = hooks;
  // eslint-disable-next-line no-console
  console.log('[testHooks] window.__appTestHooks 노출됨 — hooks:',
    Object.keys(hooks).join(', '));
};

APPLY();

export default {};
